﻿import { Dispatch, SetStateAction } from 'react';

import { Table } from 'pages/orders/fulfillment_manager/components/Table/Table';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { Api } from 'pages/orders/fulfillment_manager/Api';
import { mapToRecordObject, removeUndefined } from 'utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { Deliverable, Fulfillment } from 'types';
import { useSuperState } from 'hooks';
import { Input } from 'components';
import { useApp } from 'app';

import 'pages/orders/fulfillment_manager/components/AddDeliverables/styles/AddDeliverables.scss';

type AddDeliverablesProps = {
	deliverableTypeId?: number;
	fulfillment?: Fulfillment
	modalOpen: boolean;
	onAddClick: (deliverables: Deliverable[]) => void
	onCancelClick: () => void
	setModalOpen: Dispatch<SetStateAction<boolean>>;
    updateFulfillment: (updatedFulfillment: Fulfillment) => void;
}

export const AddDeliverables = (props: AddDeliverablesProps) => {

    const { deliverableTypeId, fulfillment, modalOpen, onAddClick, onCancelClick, setModalOpen } = props;

    const [state, setState] = useSuperState({
        deliverables: [] as Deliverable[],
        lastSearchTerm: undefined as string | undefined,
        loading: false,
        searchTerm: '',
        selectedDeliverables: {} as Record<number, (Deliverable | undefined)>
    });
    const { deliverables, lastSearchTerm, loading, searchTerm, selectedDeliverables } = state;

    const { userId } = useApp();
    const { formatMessage } = useIntl();

    const onSearchClick = async () => {

        setState({ loading: true });
        try {

            const resultingDeliverables = await Api.GetDeliverablesByFulfillmentDeliverableTypeId(
                deliverableTypeId || 1,
                searchTerm,
                userId
            );
            const currentDeliverableIds = fulfillment?.Deliverables?.map(deliverable => deliverable.Id) || [];
            setState({
                deliverables: resultingDeliverables.filter(deliverable => !currentDeliverableIds.includes(deliverable.Id)),
                lastSearchTerm: searchTerm,
                loading: false
            });

        } catch {

            setState({ loading: false });

        }

    };

    const onDeliverableCheckboxClick = (deliverable: Deliverable) => {

        setState({
            selectedDeliverables: {
                ...selectedDeliverables,
                [deliverable.Id]: selectedDeliverables[deliverable.Id] ? undefined : deliverable
            }
        });

    };

    const onHeaderCheckboxClick = () => {

        if (deliverables.every(deliverable => selectedDeliverables[deliverable.Id])){

            setState({ selectedDeliverables: {} });

        } else {

            setState({ selectedDeliverables: mapToRecordObject(deliverables, 'Id') });

        }

    };

    const getHeaderCheckboxState = () => {

        let checked = true;
        for (const deliverable of deliverables){

            if (selectedDeliverables[deliverable.Id] === undefined){

                checked = false;
                break;

            }

        }
        return checked;

    };

    const getAddButtonDisabled = () => !deliverables.some(deliverable => selectedDeliverables[deliverable.Id]);

    return (
        <GMetrixDialog
            showActionButton
            actionButtonClick={() => {

                const deliverablesToAdd = removeUndefined(Object.values(selectedDeliverables));
                onAddClick(deliverablesToAdd);

            }}
            onCancel={() => {

                setModalOpen(false);
                onCancelClick();

            }}
            actionButtonDisabled={getAddButtonDisabled()}
            actionButtonText={formatMessage({ id: 'add.e3ee51bb', defaultMessage: 'Add' })}
            show={modalOpen}
            showModalLabel={false}
        >
            <>
                <h5 className="mb-4">
                    <FormattedMessage defaultMessage="Add Items" id="add_items.7fea448c" />
                </h5>

                <div className="d-flex align-items-end gap-2 mb-4">
                    <div className="flex-grow-1">
                        <label className="form-label">

                            <FormattedMessage defaultMessage="Search" id="search.0ab06b2c" />
                        </label>
                        <Input
                            className="form-control"
                            onChange={val => setState({ searchTerm: val })}
                            value={searchTerm}
                        />
                    </div>
                    <GMetrixButton
                        className="btn btn-primary"
                        onClick={onSearchClick}
                    >
                        <FormattedMessage defaultMessage="Search" id="search.0ab06b2c" />
                    </GMetrixButton>
                </div>

                <Table
                    noDataMessage={(
                        <div className="text-center text-muted py-3">
                            <i className="fa-solid fa-search fa-2x mb-2 d-block" />
                            {
                                lastSearchTerm !== undefined
                                    ? formatMessage({ id: 'no_search_results_found_for_the_search_t.3866ec30', defaultMessage: 'No search results found for the search term "{terminput}".' }, { terminput: lastSearchTerm })
                                    : <FormattedMessage defaultMessage="Enter a search keyword above to find items to add." id="enter_a_search_keyword_above_to_find_ite.7d614f16" />
                            }
                        </div>
                    )}
                    onRowDataMap={deliverable => ({
                        cells: [
                            <input
                                checked={!!selectedDeliverables[deliverable.Id]}
                                className="form-check-input"
                                onChange={() => onDeliverableCheckboxClick(deliverable)}
                                type="checkbox"
                            />,
                            deliverable.Id,
                            deliverable.Name,
                            deliverable.Category.Name,
                            deliverable.Product?.Name,
                            deliverable.SKU,
                            deliverable.VendorName,
                            deliverable.AlternateFulfillmentKeys?.join(', ')
                        ],
                        key: deliverable.Id
                    })}
                    tableHeaders={[
                        <input
                            checked={getHeaderCheckboxState()}
                            className="form-check-input"
                            onChange={onHeaderCheckboxClick}
                            type="checkbox"
                        />,
                        formatMessage({ id: 'id.e9650a61', defaultMessage: 'ID' }),
                        formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }),
                        formatMessage({ id: 'category.f324da36', defaultMessage: 'Category' }),
                        formatMessage({ id: 'product.cf8158fa', defaultMessage: 'Product', description: 'Gridview header' }),
                        formatMessage({ id: 'sku.072d52e0', defaultMessage: 'SKU' }),
                        formatMessage({ id: 'vendor.b7db4ace', defaultMessage: 'Vendor' }),
                        formatMessage({ id: 'alternative_codes.54f96703', defaultMessage: 'Alternative Codes' })
                    ]}
                    className="table table-bordered"
                    data={deliverables}
                    header={formatMessage({ id: 'results.fc996ea3', defaultMessage: 'Results' })}
                    loading={loading}
                    onRowClick={onDeliverableCheckboxClick}
                />
            </>
        </GMetrixDialog>

    );

};